'use client';

import classNames from 'classnames';
import { useState } from 'react';
import {
  CmsCountry,
  CmsRegion,
  TravelInsuranceType,
} from '../../../../../app/[site]/[locale]/(cms)/_modules/cmsService/types';
import { TravelCompareQuotesParams } from '../../../../../app/[site]/[locale]/(cms)/_modules/travelQuotesService/types';
import FlightsMode from '../../../../icons/FlightsMode';
import Translations from './i18n';
import { TIOptionsModal } from './TIOptionsModal';
import {
  trackTripDetailsPopupClose,
  trackTripDetailsPopupOpen,
  trackTripDetailsHover,
} from './tracking';
import { TIOptionsValues } from './useTIOptions';
import Button from '$/components/Button';
import FormFieldLabel from '$/components/FormFieldLabel';
import DateRangeIcon from '$/components/icons/DateRange';
import Edit from '$/components/icons/Edit';
import Explore from '$/components/icons/Explore';
import Group from '$/components/icons/Group';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

type CompactTIOptionsProps = {
  searchParams: TravelCompareQuotesParams;
  onChangeParams: (value: TravelCompareQuotesParams) => void;
  countries: CmsCountry[];
  regions: CmsRegion[];
  boundaries: {
    minChildren?: number;
    maxChildren?: number;
    minAdult?: number;
    maxAdult?: number;
    maxTripDuration?: number;
    offsetStartDate?: number;
    tripType?: TravelInsuranceType;
    advancePurchaseDuration?: number;
    defaultAdult?: number;
    defaultChildren?: number;
    defaultTripDuration?: number;
    popularDestinations?: CmsCountry[];
    destinationCountries?: CmsCountry[];
    destinationRegion?: CmsRegion;
    excludeDestinations?: CmsCountry[];
  };
  values: TIOptionsValues;
};

const chipBgClass = 'bg-[rgba(224,244,248,0.6)]';

export const CompactTIOptions = (props: CompactTIOptionsProps) => {
  const context = useClientContext();
  const staticText = getClientTranslations(Translations, context);
  const {
    searchParams,
    onChangeParams,
    countries,
    regions,
    boundaries,
    values,
  } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    trackTripDetailsPopupOpen();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    trackTripDetailsPopupClose();
    setIsModalOpen(false);
  };

  const handleTooltipHover = (label: string) => {
    trackTripDetailsHover(label);
  };

  const { tripTypeLabel, destinationLabel, dateLabel, travelersLabel } = values;

  return (
    <>
      <div className="flex flex-col gap-spacing/gap/soft-8px lg:hidden">
        <div className="flex flex-col gap-spacing/gap/soft-6px">
          <FormFieldLabel
            label={staticText.tripDetailsLabel}
            tooltip={staticText.tripDetailsTooltip}
            onTooltipHover={() =>
              handleTooltipHover(staticText.tripDetailsLabel)
            }
          />
          <div className="flex gap-spacing/gap/soft-8px">
            <button
              type="button"
              className="flex min-h-button/size flex-1 items-center gap-spacing/gap/x-xs overflow-hidden rounded-radius/button border border-borders/form-default px-spacing/padding/soft-8px py-spacing/gap/soft-8px bg-buttons/base md:py-spacing/gap/soft-4px"
              onClick={handleOpenModal}
            >
              <div className="flex flex-1 flex-wrap items-center gap-spacing/gap/x-xs overflow-hidden">
                <div
                  className={classNames(
                    'flex items-center gap-spacing/gap/x-xs rounded-radius/tooltip p-spacing/padding/soft-4px pr-spacing/padding/x-xs text-text/accent',
                    chipBgClass,
                  )}
                >
                  <FlightsMode className="size-shapes/icon-size-md shrink-0" />
                  <span className="truncate alpha-form-input">
                    {tripTypeLabel}
                  </span>
                </div>

                {destinationLabel && (
                  <div
                    className={classNames(
                      'flex items-center gap-spacing/gap/x-xs rounded-radius/tooltip px-spacing/padding/soft-8px py-spacing/padding/soft-4px pr-spacing/padding/x-xs text-text/accent',
                      chipBgClass,
                    )}
                  >
                    <Explore className="size-shapes/icon-size-md shrink-0" />
                    <span className="truncate alpha-form-input">
                      {destinationLabel}
                    </span>
                  </div>
                )}

                {dateLabel && (
                  <div
                    className={classNames(
                      'flex items-center gap-spacing/gap/x-xs rounded-radius/tooltip px-spacing/padding/soft-8px py-spacing/padding/soft-4px pr-spacing/padding/x-xs text-text/accent',
                      chipBgClass,
                    )}
                  >
                    <DateRangeIcon className="size-shapes/icon-size-md shrink-0" />
                    <span className="truncate alpha-form-input">
                      {dateLabel}
                    </span>
                  </div>
                )}

                <div
                  className={classNames(
                    'flex items-center gap-spacing/gap/x-xs rounded-radius/tooltip px-spacing/padding/soft-8px py-spacing/padding/soft-4px pr-spacing/padding/x-xs text-text/accent',
                    chipBgClass,
                  )}
                >
                  <Group className="size-shapes/icon-size-md shrink-0" />
                  <span className="truncate alpha-form-input">
                    {travelersLabel}
                  </span>
                </div>
              </div>
            </button>

            <Button
              onClick={handleOpenModal}
              styleType="primary"
              variant="outline"
              className="size-button/size !p-0"
              aria-label={staticText.editButtonLabel}
            >
              <Edit className="size-6" />
            </Button>
          </div>
        </div>
      </div>

      <TIOptionsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        searchParams={searchParams}
        onChangeParams={onChangeParams}
        countries={countries}
        regions={regions}
        boundaries={boundaries}
        values={values}
      />
    </>
  );
};
